import { useState } from "react";
import { PageHeader } from "../../../components/PortfolioPerformance/page-header";
import { LifetimeProjectionCharts } from "../../../components/PortfolioPerformance/lifetime-projection-charts";
import type { ThreeWayBasis } from "../../../components/PortfolioPerformance/basis-toggle";
import { Panel } from "../../../components/PortfolioPerformance/panel";
import { Skeleton } from "../../../ui/skeleton";
import { IndustrySelect } from "../../../components/PortfolioPerformance/chart-filters";
import { TrendLineChart } from "../../../components/PortfolioPerformance/charts/trend-line-chart";
import {
  useLifetimeRepeatCustomerProjections,
  useRepeatCustomerSummary,
} from "../lib/api/lifetime-repeat-customer-projections";
import { useBorrowerIndustries } from "../lib/api/borrower-industries";
import { useAssetIndustries } from "../lib/api/asset-industries";

const ALL_INDUSTRIES = "All Industries";
const ALL_ASSET_INDUSTRIES = "All Asset Industries";
const ALL_BORROWER_INDUSTRIES = "All Borrower Industries";

export default function LifetimeRepeatCustomerProjectionsPage() {
  const [industry, setIndustry] = useState<string>(ALL_INDUSTRIES);
  const [basis, setBasis] = useState<ThreeWayBasis>("realized");
  const { data, isPending, isError } = useLifetimeRepeatCustomerProjections(industry, basis);
  const borrowerIndustries = useBorrowerIndustries().data ?? [];
  const assetIndustries = useAssetIndustries().data ?? [];

  // The card and the two charts under it follow these two filters (and the Ticket Size toggle); the forecast below keeps
  // its own borrower-industry dropdown, since it reads the cohort chart table.
  const [assetIndustry, setAssetIndustry] = useState<string>(ALL_ASSET_INDUSTRIES);
  const [borrowerIndustry, setBorrowerIndustry] = useState<string>(ALL_BORROWER_INDUSTRIES);
  const summary = useRepeatCustomerSummary(
    assetIndustry === ALL_ASSET_INDUSTRIES ? undefined : assetIndustry,
    borrowerIndustry === ALL_BORROWER_INDUSTRIES ? undefined : borrowerIndustry,
  );
  const s = summary.data;

  return (
    <div>
      <PageHeader
        title="Repeat Customer Projections"
        subtitle="Historical repeat-business rate, projected forward on matured cohorts, no WAL needed, a flat rate applied across the book."
      />

      <div className="mt-8 space-y-6">
        {s?.industryFilters ? (
          <div className="flex flex-wrap items-end gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Asset Industry</span>
              <IndustrySelect
                value={assetIndustry}
                options={[ALL_ASSET_INDUSTRIES, ...assetIndustries]}
                onChange={setAssetIndustry}
                ariaLabel="Asset industry"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Borrower Industry</span>
              <IndustrySelect
                value={borrowerIndustry}
                options={[ALL_BORROWER_INDUSTRIES, ...borrowerIndustries]}
                onChange={setBorrowerIndustry}
                ariaLabel="Borrower industry"
              />
            </label>
          </div>
        ) : null}

        {summary.isError ? (
          <p className="text-sm text-destructive">Could not load the repeat-customer figures.</p>
        ) : !s && summary.isPending ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Skeleton className="h-80 w-full" />
            <Skeleton className="h-80 w-full" />
          </div>
        ) : s ? (
          <>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <Panel
                title="Booking Contribution by Repeat Customers (Trailing 12 Months)"
                description="Repeat customers' 48-month business as a share of the trailing-12-month financed amount, by file date."
                right={
                  // The Historical Repeat-Business Rate: the latest value of this chart and its change, as plain highlighted text
                  <div className="text-right">
                    <p className="text-xl font-semibold tabular-nums leading-tight text-amber">{s.value}</p>
                    <p
                      className={`text-[11px] font-medium tabular-nums ${
                        s.trend.direction === "flat"
                          ? "text-muted-foreground"
                          : s.trend.direction === "up"
                            ? "text-approve"
                            : "text-decline"
                      }`}
                    >
                      {s.trend.direction === "up" ? "▲" : s.trend.direction === "down" ? "▼" : "■"} {s.trend.value}
                    </p>
                  </div>
                }
              >
                {s.contribution.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Not Available Yet.</p>
                ) : (
                  <TrendLineChart
                    data={s.contribution}
                    series={[{ dataKey: "value", name: "Repeat share", color: "var(--amber)" }]}
                    suffix="%"
                    decimals={1}
                    height={260}
                  />
                )}
              </Panel>
              <Panel
                title="Repeat Customer Trends"
                description="Share of the trailing-12-month financed amount that is repeat business, by months after origination, at the latest file date."
              >
                {s.trends.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Not Available Yet.</p>
                ) : (
                  <TrendLineChart
                    data={s.trends}
                    series={[{ dataKey: "value", name: "Repeat share", color: "hsl(var(--chart-2))", dots: true }]}
                    suffix="%"
                    decimals={1}
                    height={260}
                  />
                )}
              </Panel>
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Not Available Yet.</p>
        )}
      </div>

      <div className="mt-6">
        <LifetimeProjectionCharts
          data={data}
          isPending={isPending}
          isError={isError}
          industry={industry}
          onIndustryChange={setIndustry}
          industryOptions={[ALL_INDUSTRIES, ...borrowerIndustries]}
          basis={basis}
          onBasisChange={setBasis}
          forecastTitle="Repeat Customer Forecast"
          projectionTitle="Repeat Customer Projection"
        />
      </div>
    </div>
  );
}
